import { useEffect, useRef } from "react";
import { ReportServiceType } from "../../domain";
import { shouldShowHourInputs } from "./view-domains";

export type HourVisibility = {
    showParaprofessional: boolean;
    showCaregiverMid: boolean;
    showCaregiverHigh: boolean;
    showPractitionerMid: boolean;
    showPractitionerHigh: boolean;
    showGroupCare: boolean;
};

export type ServiceLocationFieldMapping = {
    paraprofessional?: string;
    caregiverMid?: string;
    caregiverHigh?: string;
    practitionerMid?: string;
    practitionerHigh?: string;
    groupCare?: string;
    assessment?: string;
};

export type ResetFieldOptions = {
    offeredHours?: boolean;
    hourRanges?: boolean;
    serviceLocations?: boolean;
    locationMapping?: ServiceLocationFieldMapping;
};

export function getHourVisibility(serviceTypes: ReportServiceType[]): HourVisibility {
    return {
        showParaprofessional: shouldShowHourInputs(serviceTypes, [
            'practitionerParaprofessionalHoursMin', 'practitionerParaprofessionalHoursMax',
        ]),
        showCaregiverMid: shouldShowHourInputs(serviceTypes, [
            'caregiverMidLevelHoursMin', 'caregiverMidLevelHoursMax',
        ]),
        showCaregiverHigh: shouldShowHourInputs(serviceTypes, [
            'caregiverHighLevelHoursMin', 'caregiverHighLevelHoursMax',
        ]),
        showPractitionerMid: shouldShowHourInputs(serviceTypes, [
            'practitionerMidLevelHoursMin', 'practitionerMidLevelHoursMax',
        ]),
        showPractitionerHigh: shouldShowHourInputs(serviceTypes, [
            'practitionerHighLevelHoursMin', 'practitionerHighLevelHoursMax',
        ]),
        showGroupCare: shouldShowHourInputs(serviceTypes, [
            'practitionerGroupCareHoursMin', 'practitionerGroupCareHoursMax',
        ]),
    };
}

export function getFieldsToResetForHourVisibility(
    visibility: HourVisibility,
    options: ResetFieldOptions = { offeredHours: true, hourRanges: true, serviceLocations: true },
): string[] {
    const {
        showParaprofessional,
        showCaregiverMid,
        showCaregiverHigh,
        showPractitionerMid,
        showPractitionerHigh,
        showGroupCare,
    } = visibility;

    const fields: string[] = [];

    if (options.offeredHours) {
        if (!showParaprofessional) {
            fields.push('offeredParaprofessionalHours');
        }
        if (!showCaregiverMid && !showPractitionerMid) {
            fields.push('offeredMidLevelHours');
        }
        if (!showCaregiverHigh && !showPractitionerHigh) {
            fields.push('offeredHighLevelHours');
        }
        if (!showGroupCare) {
            fields.push('offeredGroupCareHours');
        }
    }

    if (options.hourRanges) {
        if (!showParaprofessional) {
            fields.push('practitionerParaprofessionalHoursMin', 'practitionerParaprofessionalHoursMax');
        }
        if (!showCaregiverMid) {
            fields.push('caregiverMidLevelHoursMin', 'caregiverMidLevelHoursMax');
        }
        if (!showCaregiverHigh) {
            fields.push('caregiverHighLevelHoursMin', 'caregiverHighLevelHoursMax');
        }
        if (!showPractitionerMid) {
            fields.push('practitionerMidLevelHoursMin', 'practitionerMidLevelHoursMax');
        }
        if (!showPractitionerHigh) {
            fields.push('practitionerHighLevelHoursMin', 'practitionerHighLevelHoursMax');
        }
        if (!showGroupCare) {
            fields.push('practitionerGroupCareHoursMin', 'practitionerGroupCareHoursMax');
        }
    }

    if (options.serviceLocations && options.locationMapping) {
        const mapping = options.locationMapping;
        if (!showParaprofessional && mapping.paraprofessional) {
            fields.push(mapping.paraprofessional);
        }
        if (!showPractitionerMid && !showCaregiverMid && mapping.practitionerMid) {
            fields.push(mapping.practitionerMid);
        }
        if (!showPractitionerHigh && !showCaregiverHigh && mapping.practitionerHigh) {
            fields.push(mapping.practitionerHigh);
        }
        if (!showGroupCare && mapping.groupCare) {
            fields.push(mapping.groupCare);
        }
    }

    return fields;
}

export function useResetHiddenFieldsOnServiceTypeChange(
    serviceType: string[] | null | undefined,
    setValue: (field: string, value: null) => void,
    options: ResetFieldOptions,
    enabled: boolean = true,
): void {
    const previousServiceTypeRef = useRef<string[] | null>(null);

    useEffect(() => {
        const current = serviceType ?? [];
        const previous = previousServiceTypeRef.current;
        const hasChanged = previous !== null && (
            previous.length !== current.length || current.some((value, index) => value !== previous[index])
        );

        if (enabled && hasChanged) {
            const fields = getFieldsToResetForHourVisibility(
                getHourVisibility(current as ReportServiceType[]),
                options,
            );
            fields.forEach((field) => setValue(field, null));
        }

        previousServiceTypeRef.current = current;
    }, [serviceType, setValue, options, enabled]);
}
